import { User as Auth0User } from '@auth0/auth0-react'

import {
    GlobalUserReadDto,
    PermissionSuffix,
    TenantUserReadDto,
} from '../_generated/api/user-management'
import ApiModel from './ApiModel'
import { nullableDate } from './ApiLifeCycleModel'
import { buildNavigationLink } from '../tools/link'
import t from '../tools/t'

export const generateUserInitials = (name: string) => {
    // The common unknown string may be used in place of a user name.
    // In these cases we want to show a ? instead of U for initials.
    if (name === t('common_unknown')) return '?'
    const names = name.split(' ').filter((s) => !!s)
    if (names.length === 0) return '?'
    else if (names.length === 1) return names[0].charAt(0)
    else return names[0].charAt(0) + names[1].charAt(0)
}

const prefixPermission = (tenantId: string) => `urn:tenant:${tenantId}:`
const checkPermission = (permissions: string[], permission: PermissionSuffix) =>
    permissions.some((p) => p.endsWith(permission))

export default class User extends ApiModel<TenantUserReadDto> {
    get id() {
        return this.data.id ?? ''
    }

    get name() {
        return this.data.name ?? ''
    }

    get email() {
        return this.data.email ?? ''
    }

    get association() {
        return this.data.vatNo ?? ''
    }

    get signedEula() {
        return this.data.signedEula ?? undefined
    }

    get permissions(): PermissionSuffix[] {
        const permissions = this.data.permissions ?? []
        const suffixes = Object.values(PermissionSuffix)
        return suffixes.filter((suffix) => permissions.find((p) => p?.includes(suffix)))
    }

    get isAdmin() {
        return false
    }

    get profileLink() {
        return buildNavigationLink('users', this.id)
    }

    get initials() {
        return generateUserInitials(this.name)
    }

    get archivedAt() {
        return nullableDate(this.data.archivedAt)
    }

    get isArchived() {
        return !!this.archivedAt
    }

    constructor(public user: TenantUserReadDto) {
        super(user)
    }
}

export class Me {
    get id() {
        return this.identity.id ?? ''
    }

    get name() {
        return this.identity.name ?? ''
    }

    get email() {
        return this.identity.email ?? ''
    }

    get ownerTenantId() {
        return this.identity.ownerTenant ?? ''
    }

    get ownerTenant() {
        const tenant = this.ownerTenantId
        if (!tenant) return this.identity?.tenantIdentities?.[0]
        return this.getIdentityFor(tenant)
    }

    get isAdmin() {
        return this.identity?.isAdmin || false
    }

    get association() {
        return this.ownerTenant?.vatNo ?? undefined
    }

    get picture() {
        return this.auth0User?.picture
    }

    get initials() {
        return generateUserInitials(this.name)
    }

    get signedEula() {
        return this.identity.signedEula ?? undefined
    }

    get profileLink() {
        return buildNavigationLink('users', this.id)
    }

    constructor(
        public identity: GlobalUserReadDto,
        private auth0User?: Auth0User
    ) {}

    getIdentityFor(tenantId: string) {
        if (!tenantId) return undefined
        return this.identity?.tenantIdentities?.find((t) => t.tenantId === tenantId)
    }

    getAssociationFor(tenantId: string) {
        const identity = this.getIdentityFor(tenantId)
        return identity?.vatNo
    }

    getPermissionsFor(tenantId: string) {
        const permissionPrefix = prefixPermission(tenantId)
        return this.identity?.permissions?.filter((p) => p.startsWith(permissionPrefix)) ?? []
    }

    getAdminPermissions() {
        return this.getPermissionsFor('*')
    }

    hasAdminPermission(permission: PermissionSuffix) {
        return checkPermission(this.getAdminPermissions(), permission)
    }

    hasTenantPermission(tenantId: string | undefined, permission: PermissionSuffix) {
        if (!tenantId) return false
        return checkPermission(this.getPermissionsFor(tenantId), permission)
    }

    hasPermission(tenantId: string | undefined, permission: PermissionSuffix) {
        const admin = this.hasAdminPermission(permission)
        // NOTE: If the user has admin permissions, there is no reason to check other permissions.
        if (admin) return true
        return this.hasTenantPermission(tenantId, permission)
    }
}
